import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { CashflowAllocation } from "models/CashflowModel";
import { formatCurrency, formatCurrencyExact, formatMonthLabel, formatPercentage } from "../utils";

interface MonthlyDetailTableProps {
  monthKey: string;
  entries: CashflowAllocation[];
  onTogglePaid?: (poNumber: string, trancheOrder: number, paid: boolean) => void;
  isUpdatingPaid?: boolean;
}

type SortField = "poNumber" | "supplierName" | "brand" | "paymentType" | "status" | "trancheAmount";

export default function MonthlyDetailTable({
  monthKey,
  entries,
  onTogglePaid,
  isUpdatingPaid,
}: MonthlyDetailTableProps) {
  const [sortField, setSortField] = useState<SortField>("supplierName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case "trancheAmount":
          aVal = a.trancheAmount;
          bVal = b.trancheAmount;
          break;
        default:
          aVal = (a[sortField] || "").toString().toLowerCase();
          bVal = (b[sortField] || "").toString().toLowerCase();
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [entries, sortField, sortDirection]);

  // Group by supplier for subtotals
  const supplierSubtotals = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of entries) {
      map[e.supplierName] = (map[e.supplierName] || 0) + e.trancheAmount;
    }
    return map;
  }, [entries]);

  const grandTotal = entries.reduce((sum, e) => sum + e.trancheAmount, 0);

  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="subtitle2">
          {formatMonthLabel(monthKey)} Detail
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortField === "poNumber"}
                  direction={sortField === "poNumber" ? sortDirection : "asc"}
                  onClick={() => handleSort("poNumber")}
                >
                  PO Number
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "supplierName"}
                  direction={sortField === "supplierName" ? sortDirection : "asc"}
                  onClick={() => handleSort("supplierName")}
                >
                  Supplier
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "brand"}
                  direction={sortField === "brand" ? sortDirection : "asc"}
                  onClick={() => handleSort("brand")}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "paymentType"}
                  direction={sortField === "paymentType" ? sortDirection : "asc"}
                  onClick={() => handleSort("paymentType")}
                >
                  Payment Type
                </TableSortLabel>
              </TableCell>
              <TableCell>Trigger Event</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === "status"}
                  direction={sortField === "status" ? sortDirection : "asc"}
                  onClick={() => handleSort("status")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {onTogglePaid && <TableCell align="center">Paid</TableCell>}
              <TableCell align="right">Base Amount</TableCell>
              <TableCell align="right">Tariff</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === "trancheAmount"}
                  direction={sortField === "trancheAmount" ? sortDirection : "asc"}
                  onClick={() => handleSort("trancheAmount")}
                >
                  Payment
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.map((entry, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {entry.poNumber}
                  </Typography>
                </TableCell>
                <TableCell>{entry.supplierName}</TableCell>
                <TableCell>{entry.brand || "-"}</TableCell>
                <TableCell>{entry.paymentType} ({formatPercentage(entry.percentage)})</TableCell>
                <TableCell>{entry.triggerEvent}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={entry.status}
                    color={entry.status === "placed" ? "primary" : "default"}
                    variant="outlined"
                  />
                </TableCell>
                {onTogglePaid && (
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={!!entry.paid}
                      onChange={(e) => {
                        if (entry.trancheOrder != null) {
                          onTogglePaid(entry.poNumber, entry.trancheOrder, e.target.checked);
                        }
                      }}
                      disabled={entry.trancheOrder == null || isUpdatingPaid}
                    />
                  </TableCell>
                )}
                <TableCell align="right">{formatCurrency(entry.baseAmount)}</TableCell>
                <TableCell align="right">
                  {entry.tariffRate > 0 ? formatPercentage(entry.tariffRate) : "-"}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatCurrencyExact(entry.trancheAmount)}
                </TableCell>
              </TableRow>
            ))}

            {/* Supplier subtotal rows */}
            {Object.keys(supplierSubtotals).length > 1 && (
              <>
                {Object.entries(supplierSubtotals).sort((a, b) => a[0].localeCompare(b[0])).map(([supplier, total]) => (
                  <TableRow key={`subtotal-${supplier}`} sx={{ backgroundColor: "action.hover" }}>
                    <TableCell />
                    <TableCell sx={{ fontWeight: 600 }}>{supplier} Subtotal</TableCell>
                    <TableCell colSpan={onTogglePaid ? 6 : 5} />
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrencyExact(total)}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}

            {/* Grand total */}
            <TableRow sx={{ backgroundColor: "action.selected" }}>
              <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>
              <TableCell colSpan={onTogglePaid ? 7 : 6} />
              <TableCell />
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatCurrencyExact(grandTotal)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
